import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.95); /* More opaque for better coverage */
  backdrop-filter: blur(8px);
  z-index: ${({ $zIndex }) => $zIndex || 1500};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: hidden;
  
  @media (max-width: 767px) {
    align-items: flex-start;
    padding: 0;
    background: rgba(15, 23, 42, 1); /* Fully opaque on mobile */
  }
`;

const Container = styled(motion.div)`
  background: ${({ theme }) => theme.colors.gradient.card};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 32px;
  max-width: ${({ $maxWidth }) => $maxWidth || '480px'};
  width: 100%;
  max-height: calc(100vh - 40px);
  box-shadow: ${({ theme }) => theme.shadows.prominent};
  position: relative;
  margin: auto;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  /* Mobile optimizations */
  @media (max-width: 767px) {
    padding: 24px 20px;
    max-height: 100vh;
    max-height: 100dvh; /* Use dynamic viewport height */
    border-radius: ${({ theme }) => theme.borderRadius.xl} ${({ theme }) => theme.borderRadius.xl} 0 0;
    margin-top: 0;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 20px 16px;
    max-height: 100vh;
    max-height: 100dvh; /* Use dynamic viewport height */
    border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
  }
  
  /* Gradient top border */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradient.primary};
    border-radius: ${({ theme }) => theme.borderRadius.xl} ${({ theme }) => theme.borderRadius.xl} 0 0;
  }
`;

const DecorativeCircle = styled.div`
  position: absolute;
  top: -30px;
  right: -30px;
  width: 120px;
  height: 120px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  opacity: 0.1;
  filter: blur(30px);
  pointer-events: none;
`;

/**
 * BaseModal - Unified popup/modal component with standardized schema
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal should close (click-outside or ESC)
 * @param {ReactNode} children - Modal content
 * @param {number} zIndex - Z-index for overlay (default: 1500)
 * @param {string} maxWidth - Max width of container (default: '480px')
 * @param {boolean} showDecorativeElements - Show decorative circle (default: false)
 * @param {boolean} closeOnEscape - Close on ESC key (default: true)
 * @param {boolean} closeOnClickOutside - Close on overlay click (default: true)
 */
export const BaseModal = ({
  isOpen,
  onClose,
  children,
  zIndex = 1500,
  maxWidth = '480px',
  showDecorativeElements = false,
  closeOnEscape = true,
  closeOnClickOutside = true,
}) => {
  // Handle ESC key press
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle click outside
  const handleOverlayClick = (e) => {
    if (closeOnClickOutside && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          $zIndex={zIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={handleOverlayClick}
        >
          <Container
            $maxWidth={maxWidth}
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {showDecorativeElements && <DecorativeCircle />}
            {children}
          </Container>
        </Overlay>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default BaseModal;

